// ============================================================
// App route table — single source of truth for routing.
// 選用組件的路由由 compose 在「@module」標記行下注入。
// ============================================================
import type { ReactElement } from 'react';
import { Navigate } from 'react-router-dom';

// Customer pages
import Home from '../pages/Home';
import Story from '../pages/Story';
import MediaCoverage from '../pages/MediaCoverage';
import ProductDetail from '../pages/ProductDetail';
import Store from '../pages/Store';
import Cart from '../pages/Cart';
import Checkout from '../pages/Checkout';
import CheckoutConfirm from '../pages/CheckoutConfirm';
import OrderSuccess from '../pages/OrderSuccess';
import OrderQuery from '../pages/OrderQuery';
import PaymentProof from '../pages/PaymentProof';
import Terms from '../pages/Terms';
import Privacy from '../pages/Privacy';
// @module:imports:customer — 選用組件的顧客頁 import由 compose 注入於此行之下
import SupplierSubmitProduct from '../pages/SupplierSubmitProduct';
import ReferralDashboard from '../pages/ReferralDashboard';
import Offers from '../pages/Offers';
import Redeem from '../pages/Redeem';

// Legacy / compatibility pages
import OrderConfirmation from '../pages/OrderConfirmation';
import PaymentProofUpload from '../pages/PaymentProofUpload';

// Admin pages
import AdminLogin from '../pages/AdminLogin';
import AdminDashboard from '../pages/AdminDashboard';
import AdminOrders from '../pages/AdminOrders';
import AdminOrderDetail from '../pages/AdminOrderDetail';
import AdminProducts from '../pages/AdminProducts';
import AdminUsers from '../pages/AdminUsers';
import AdminPromoCodes from '../pages/AdminPromoCodes';
import AdminMediaLibrary from '../pages/AdminMediaLibrary';
import AdminConversations from '../pages/AdminConversations';
import AdminSettings from '../pages/AdminSettings';
import AdminAuditLogs from '../pages/AdminAuditLogs';
import AdminCMS from '../pages/AdminCMS';
import AdminAnalytics from '../pages/AdminAnalytics';
import AdminAhJimContent from '../pages/AdminAhJimContent';
// @module:imports:admin — 選用組件的後台頁 import由 compose 注入於此行之下
import AdminSupplier from '../pages/AdminSupplier';
import AdminPackageConfigs from '../pages/AdminPackageConfigs';
import AdminJourneys from '../pages/AdminJourneys';
import AdminReferrals from '../pages/AdminReferrals';
import AdminReferralDetail from '../pages/AdminReferralDetail';
import AdminReferralTree from '../pages/AdminReferralTree';
import AdminOffers from '../pages/AdminOffers';

export interface AppRoute {
  path: string;
  element: ReactElement;
  /** 顧客頁顯示名稱（供日後彈窗等組件派生頁面清單用） */
  label?: string;
}

export const APP_ROUTES: AppRoute[] = [
  // Customer routes
  { path: '/', element: <Home />, label: '首頁' },
  { path: '/story', element: <Story />, label: '品牌故事' },
  { path: '/media-coverage', element: <MediaCoverage />, label: '媒體報導' },
  { path: '/products', element: <Navigate to="/store" replace /> },
  { path: '/products/:slug', element: <ProductDetail />, label: '產品詳情' },
  { path: '/store', element: <Store />, label: '商店' },
  { path: '/cart', element: <Cart />, label: '購物車' },
  { path: '/checkout', element: <Checkout />, label: '結帳' },
  { path: '/checkout/confirm/:orderNum', element: <CheckoutConfirm /> },
  { path: '/order/success', element: <OrderSuccess /> },
  { path: '/order/:orderNum', element: <OrderQuery />, label: '訂單頁' },
  { path: '/o/:orderNum', element: <OrderSuccess />, label: '訂單短連結' },
  { path: '/payment-proof/:orderNum', element: <PaymentProof />, label: '付款憑證' },
  { path: '/terms', element: <Terms />, label: '條款' },
  { path: '/privacy', element: <Privacy />, label: '隱私' },
  // @module:routes:customer — 選用組件的顧客路由由 compose 注入於此行之下
  { path: '/supplier/submit-product', element: <SupplierSubmitProduct />, label: '供貨商提交產品' },
  { path: '/supplier/portal', element: <SupplierSubmitProduct />, label: '供貨商門戶' },
  { path: '/ref/d/:token', element: <ReferralDashboard />, label: '推薦看板' },
  { path: '/offers', element: <Offers />, label: '優惠' },
  { path: '/offers/:key', element: <Offers /> },
  { path: '/c/:key', element: <Offers /> },
  { path: '/redeem/:code', element: <Redeem />, label: '優惠兌換頁' },
  { path: '/r/:code', element: <Redeem /> },

  // Legacy / compatibility routes
  { path: '/order/success-legacy', element: <OrderConfirmation /> },
  { path: '/payment-proof-upload/:orderNum', element: <PaymentProofUpload /> },

  // Admin routes
  { path: '/admin', element: <AdminLogin /> },
  { path: '/admin/dashboard', element: <AdminDashboard /> },
  { path: '/admin/orders', element: <AdminOrders /> },
  { path: '/admin/orders/:id', element: <AdminOrderDetail /> },
  { path: '/admin/products', element: <AdminProducts /> },
  { path: '/admin/promo-codes', element: <AdminPromoCodes /> },
  { path: '/admin/users', element: <AdminUsers /> },
  { path: '/admin/media-library', element: <AdminMediaLibrary /> },
  { path: '/admin/conversations', element: <AdminConversations /> },
  { path: '/admin/settings', element: <AdminSettings /> },
  { path: '/admin/audit-logs', element: <AdminAuditLogs /> },
  { path: '/admin/cms', element: <AdminCMS /> },
  { path: '/admin/analytics', element: <AdminAnalytics /> },
  { path: '/admin/default-responses', element: <AdminAhJimContent /> },
  // @module:routes:admin — 選用組件的後台路由由 compose 注入於此行之下
  { path: '/admin/supplier', element: <AdminSupplier /> },
  { path: '/admin/package-configs', element: <AdminPackageConfigs /> },
  { path: '/admin/journeys', element: <AdminJourneys /> },
  { path: '/admin/referrals', element: <AdminReferrals /> },
  { path: '/admin/referrals/:phone', element: <AdminReferralDetail /> },
  { path: '/admin/referrals/:phone/tree', element: <AdminReferralTree /> },
  { path: '/admin/offers', element: <AdminOffers /> },
];
